import {$isAtNodeEnd} from '@lexical/selection';
import {WorkstreamActionType} from 'client';
import {COMMENT_DISPLAY_WIDTH_PX} from 'components/DSYS/editor/plugins/commentDisplaySizing';
import {t} from 'i18next';
import {RangeSelection} from 'lexical';

export const LowPriority = 1;

export type WorkstreamNodeType =
  | 'switchboard-link'
  | 'switchboard-name'
  | 'switchboard-objective'
  | 'switchboard-objective-editable'
  | 'switchboard-dri'
  | 'switchboard-exec-sponsor'
  | 'switchboard-working-group'
  | 'switchboard-eta'
  | 'switchboard-subworkstreams'
  | 'workstream-action'
  | 'workstream-link'
  | 'workstream-name'
  | 'workstream-objective'
  | 'workstream-objective-editable'
  | 'workstream-outcomes'
  | 'workstream-metrics'
  | 'workstream-primary-lead-metric'
  | 'workstream-primary-lag-metric'
  | 'workstream-starred-milestones'
  | 'workstream-definition-of-done'
  | 'workstream-dri'
  | 'workstream-exec-sponsor'
  | 'workstream-eta'
  | 'workstream-metric'
  | 'workstream-metric-dri'
  | 'workstream-metric-current-value'
  | 'workstream-metric-target-value'
  | 'workstream-metric-eta'
  | 'workstream-subworkstreams'
  | 'contributing-workstream-name'
  | 'contributing-workstream-subworkstreams';

export function getSelectedNode(selection: RangeSelection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

export function getWorkstreamActionText(
  actionType: WorkstreamActionType | null | undefined
): string {
  return actionType === 'SWITCHBOARD'
    ? t('typeahead_action_type_switchboard')
    : actionType === 'REFRESH'
      ? t('typeahead_action_type_refresh')
      : t('typeahead_action_type_default'); // for actionType === NONE or null/undefined
}

export function getWorkstreamActionIcon(
  actionType: WorkstreamActionType | null | undefined
): string {
  return actionType === 'SWITCHBOARD' ? `⭐ ` : actionType === 'REFRESH' ? `🔁 ` : `♾️ `;
}

export function isLexicalValueEmpty(value: string | null | undefined): boolean {
  if (!value) return true;

  try {
    const parsed = JSON.parse(value);
    const root = parsed?.root;

    if (!root) return true;

    const children = root.children || [];
    if (children.length === 0) return true;

    if (
      children.length === 1 &&
      children[0].type === 'paragraph' &&
      (!children[0].children || children[0].children.length === 0)
    ) {
      return true;
    }

    return false;
  } catch {
    return true;
  }
}

export const containsMultipleThreads = (
  threadElement: Element | null,
  activeThreadIds: string[]
) => {
  const threadIds = threadElement?.getAttribute('data-highlight-id')?.split(' ') ?? [];
  if (activeThreadIds.length) {
    const filteredActiveThreadIds = threadIds.filter((id) => activeThreadIds.includes(id));
    return filteredActiveThreadIds.length > 1;
  }
  return threadIds.length > 1;
};

/**
 * Returns true if `node` lives inside a `[data-lexical-editor]` that is a
 * strict descendant of `ownRoot` (i.e. a nested Lexical editor).
 */
export function isInsideNestedEditor(node: Node | null, ownRoot: HTMLElement | null): boolean {
  if (!node || !ownRoot) {
    return false;
  }
  const el =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : (node.parentElement as Element | null);
  if (!el) {
    return false;
  }
  const nearestEditor = el.closest('[data-lexical-editor]');
  return Boolean(nearestEditor && nearestEditor !== ownRoot && ownRoot.contains(nearestEditor));
}

/**
 * Finds a comment highlight span under `editorRoot` that belongs to this editor
 * (not inside a nested `[data-lexical-editor]`). `closest('[data-lexical-editor]') === editorRoot`
 * is unreliable when the inner ContentEditable is wrapped (e.g. Box); walking containment +
 * nested exclusion matches Lexical's actual DOM.
 */
export function findHighlightSpanOwnedByEditor(
  editorRoot: HTMLElement | null,
  threadId: string
): HTMLElement | null {
  if (!editorRoot) {
    return null;
  }
  const safeId =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(threadId)
      : threadId;
  const spans = editorRoot.querySelectorAll<HTMLElement>(`span[data-highlight-id~="${safeId}"]`);
  for (const span of spans) {
    if (!editorRoot.contains(span)) {
      continue;
    }
    if (isInsideNestedEditor(span, editorRoot)) {
      continue;
    }
    return span;
  }
  return null;
}

/**
 * Fixed-position coordinates for a nested-editor CommentDisplay anchored below an element.
 * Centers a `width` box horizontally under `anchorRect` with `gap` between them, and clamps
 * the result to the viewport with `viewportPadding` on all sides. Falls back to above the
 * anchor when there isn't enough room below. Mirrors the Floating UI offset(10) + shift()
 * behavior used by the top-level editor, so nested comment boxes read the same visually.
 */
export function getNestedCommentDisplayPosition(
  anchorRect: DOMRect,
  maxHeight: number | null | undefined,
  options: {width?: number; gap?: number; viewportPadding?: number} = {}
): {top: number; left: number; width: number} {
  const width = options.width ?? COMMENT_DISPLAY_WIDTH_PX;
  const gap = options.gap ?? 10;
  const viewportPadding = options.viewportPadding ?? 16;
  const estimatedHeight = Math.min(maxHeight ?? 360, 480);

  const spaceBelow = window.innerHeight - anchorRect.bottom - viewportPadding;
  const spaceAbove = anchorRect.top - viewportPadding;
  const preferBelow = spaceBelow >= estimatedHeight + gap || spaceBelow >= spaceAbove;

  const preferredTop = preferBelow
    ? anchorRect.bottom + gap
    : anchorRect.top - gap - estimatedHeight;

  const top = Math.max(
    viewportPadding,
    Math.min(preferredTop, window.innerHeight - estimatedHeight - viewportPadding)
  );

  const centeredLeft = anchorRect.left + anchorRect.width / 2 - width / 2;
  const left = Math.max(
    viewportPadding,
    Math.min(centeredLeft, window.innerWidth - width - viewportPadding)
  );

  return {top, left, width};
}
